import React, { useEffect, useState } from "react"
import { KeyboardAvoidingView, TouchableOpacity, View } from "react-native"
import { useAnalysisData } from "@/hooks/useAnalysisData"
import CloseButton from "@/assets/icons/close-button.svg"
import CalculatorItem from "@/components/calculate/CalculatorItem"
import { PrimaryButton } from "@/components/buttons/CommonButtons"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import * as AnalyzerEvents from "@/analytics/events/analyzer"
import { type AnalysisType } from "@/generated/graphql"
import BFText, { Body } from "@/components/standard/BFText"
import { AddressDisplay } from "@/components/AddressDisplay"

interface PropertyValues {
  bedrooms: number | null
  bathrooms: number | null
  area: number | null
  purchasePrice: number | null
}

interface PropertyEditModalProps {
  analysisId?: string | null
  street: string
  cityStateZip: string
  onEditProperty: (
    bedrooms: number,
    bathrooms: number,
    area: number,
    purchasePrice?: number
  ) => void
  isRequiredEdit: boolean
  onClose: () => void
}

const PropertyEditModal: React.FC<PropertyEditModalProps> = ({
  analysisId,
  street,
  cityStateZip,
  onEditProperty,
  isRequiredEdit = true,
  onClose,
}: PropertyEditModalProps) => {
  const [currentValues, setCurrentValues] = useState<PropertyValues>({
    bedrooms: null,
    bathrooms: null,
    area: null,
    purchasePrice: null,
  })

  const insets = useSafeAreaInsets()
  const { analysis } = useAnalysisData(analysisId || undefined)

  useEffect(() => {
    if (analysis) {
      setCurrentValues({
        bedrooms: analysis.bedrooms || 0,
        bathrooms: analysis.bathrooms || 0,
        area: analysis.area || 0,
        purchasePrice: analysis.purchasePrice || 0,
      })

      AnalyzerEvents.editSubjectPropertyViewed({
        analysis,
      })
    }
  }, [analysis?.id])

  const handleValueChange = (
    field: keyof PropertyValues,
    value: number | null
  ) => {
    setCurrentValues((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  // Save changes and trigger analytics events for changed fields only
  const onSave = () => {
    if (analysis) {
      Object.entries(currentValues).forEach(([key, newValue]) => {
        const oldValue = analysis[key as keyof AnalysisType]
        if (newValue !== oldValue) {
          AnalyzerEvents.analysisEditSubmitted({
            analysis,
            editedField: { fieldName: key, newValue },
            fromScreen: "Edit Subject Property",
            toScreen: "Subject Property",
          })
        }
      })
    }

    if (isRequiredEdit) {
      onEditProperty(
        currentValues.bedrooms ?? 0,
        currentValues.bathrooms ?? 0,
        currentValues.area ?? 0
      )
    } else {
      onEditProperty(
        currentValues.bedrooms ?? 0,
        currentValues.bathrooms ?? 0,
        currentValues.area ?? 0,
        currentValues.purchasePrice ?? undefined
      )
    }
  }

  const handleClosePress = () => {
    onClose()
  }

  const disableSave =
    !currentValues.bedrooms ||
    !currentValues.bathrooms ||
    !currentValues.area ||
    (!isRequiredEdit && !currentValues.purchasePrice)

  return (
    <KeyboardAvoidingView
      className="flex-1 p-2 bg-off-white"
      behavior="padding"
      style={{
        paddingTop: insets.top,
      }}
    >
      <View className="flex items-end mr-4">
        <TouchableOpacity
          testID={"close-button"}
          onPress={handleClosePress}
          hitSlop={32}
        >
          <CloseButton width={13} height={13} />
        </TouchableOpacity>
      </View>
      <View className="mx-4">
        <AddressDisplay
          theme={"dark"}
          street={street}
          cityStateZip={cityStateZip}
        />
      </View>
      {!isRequiredEdit && (
        <BFText className="text-black text-[20px] font-figtree-extrabold leading-tight tracking-tight mx-4 mb-1">
          Subject Property Edits
        </BFText>
      )}
      {isRequiredEdit && (
        <Body className="text-midnight-blue mx-4">
          We need additional information for this property, please confirm/enter
          the following:
        </Body>
      )}
      <View className="p-4 m-4 bg-white rounded-md border border-gray-200">
        <CalculatorItem
          title="Bed"
          value={currentValues.bedrooms}
          prefix=""
          onValueChange={(value) => {
            handleValueChange("bedrooms", value)
          }}
          isError={!currentValues.bedrooms}
        />
        <CalculatorItem
          title="Bath"
          value={currentValues.bathrooms}
          prefix=""
          precision={1}
          onValueChange={(value) => {
            handleValueChange("bathrooms", value)
          }}
          isError={!currentValues.bathrooms}
        />
        <CalculatorItem
          title="Sq Ft"
          value={currentValues.area}
          prefix=""
          onValueChange={(value) => {
            handleValueChange("area", value)
          }}
          isError={!currentValues.area}
        />
        {!isRequiredEdit && (
          <CalculatorItem
            title="Purchase Price"
            value={currentValues.purchasePrice}
            prefix="$"
            onValueChange={(value) => {
              handleValueChange("purchasePrice", value)
            }}
            isError={!currentValues.purchasePrice}
          />
        )}
      </View>
      <View className="justify-end flex-1 m-6 mb-12">
        <PrimaryButton
          testID={"save-button"}
          title="Save"
          onPress={onSave}
          disabled={disableSave}
        />
      </View>
    </KeyboardAvoidingView>
  )
}

export default PropertyEditModal
